/**
 * Request Cache & Throttling System
 * Prevents rate limit errors by caching database responses and throttling requests
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

interface RequestQueue {
  promise: Promise<any>;
  timestamp: number;
}

class RequestCacheManager {
  private cache = new Map<string, CacheEntry<any>>();
  private requestQueue = new Map<string, RequestQueue>();
  private defaultTTL = 60000; // 1 minute default cache
  private requestDelay = 100; // 100ms minimum between requests
  private lastRequestTime = 0;

  /**
   * Get cached data if available and not expired
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    
    if (!entry) {
      return null;
    }
    
    // Check if expired
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return null;
    }
    
    return entry.data as T;
  }

  /**
   * Set cache entry with TTL
   */
  set<T>(key: string, data: T, ttl: number = this.defaultTTL): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + ttl,
    });
  }

  /**
   * Clear specific cache entry or pattern
   * Supports wildcards: clear('emails:*') clears all keys starting with 'emails:'
   */
  clear(keyPattern: string): void {
    // Support wildcard clearing
    if (keyPattern.includes('*')) {
      const regex = new RegExp('^' + keyPattern.replace(/\*/g, '.*') + '$');
      const keysToDelete: string[] = [];
      
      for (const key of this.cache.keys()) {
        if (regex.test(key)) {
          keysToDelete.push(key);
        }
      }
      
      keysToDelete.forEach(key => this.cache.delete(key));
      
      if (keysToDelete.length > 0) {
        console.log(`🗑️  Cleared ${keysToDelete.length} cache entries matching pattern: ${keyPattern}`);
      }
    } else {
      this.cache.delete(keyPattern);
    }
  }

  /**
   * Clear all cache entries
   */
  clearAll(): void {
    this.cache.clear();
  }

  /**
   * Clear expired entries
   */
  clearExpired(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now > entry.expiresAt) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Throttle requests to prevent rate limiting
   * Returns a promise that resolves after the minimum delay
   */
  private async throttle(): Promise<void> {
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    
    if (timeSinceLastRequest < this.requestDelay) {
      const delay = this.requestDelay - timeSinceLastRequest;
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    
    this.lastRequestTime = Date.now();
  }

  /**
   * Execute a request with caching and throttling
   * Deduplicates identical concurrent requests
   */
  async fetchWithCache<T>(
    key: string,
    fetcher: () => Promise<T>,
    options: {
      ttl?: number;
      forceRefresh?: boolean;
      skipThrottle?: boolean;
    } = {}
  ): Promise<T> {
    const { ttl = this.defaultTTL, forceRefresh = false, skipThrottle = false } = options;

    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = this.get<T>(key);
      if (cached !== null) {
        return cached;
      }
    }

    // Check if there's already a pending request for this key
    const pendingRequest = this.requestQueue.get(key);
    if (pendingRequest) {
      return pendingRequest.promise;
    }

    // Throttle the request
    if (!skipThrottle) {
      await this.throttle();
    }

    // Create and store the promise
    const promise = fetcher()
      .then(data => {
        // Cache the result
        this.set(key, data, ttl);
        // Remove from queue
        this.requestQueue.delete(key);
        return data;
      })
      .catch(error => {
        // Remove from queue on error
        this.requestQueue.delete(key);
        throw error;
      });

    // Store in queue to deduplicate concurrent requests
    this.requestQueue.set(key, {
      promise,
      timestamp: Date.now(),
    });

    return promise;
  }

  /**
   * Retry a request with exponential backoff
   */
  async retryWithBackoff<T>(
    fetcher: () => Promise<T>,
    options: {
      maxRetries?: number;
      initialDelay?: number;
      maxDelay?: number;
      shouldRetry?: (error: any) => boolean;
    } = {}
  ): Promise<T> {
    const {
      maxRetries = 3,
      initialDelay = 1000,
      maxDelay = 10000,
      shouldRetry = (error: any) => error?.status === 429,
    } = options;

    let lastError: any;
    let delay = initialDelay;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await fetcher();
      } catch (error: any) {
        lastError = error;

        // Don't retry if this is the last attempt or if we shouldn't retry this error
        if (attempt === maxRetries || !shouldRetry(error)) {
          throw error;
        }

        // Extract retry delay from error if available (429 errors often include this)
        const retryAfter = error?.details?.reset
          ? new Date(error.details.reset).getTime() - Date.now()
          : delay;

        const actualDelay = Math.min(Math.max(retryAfter, delay), maxDelay);

        console.log(`⏳ Rate limited. Retrying in ${Math.round(actualDelay / 1000)}s (attempt ${attempt + 1}/${maxRetries})...`);

        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, actualDelay));

        // Exponential backoff
        delay = Math.min(delay * 2, maxDelay);
      }
    }

    throw lastError;
  }

  /**
   * Get cache statistics
   */
  getStats() {
    const now = Date.now();
    let activeEntries = 0;
    let expiredEntries = 0;

    for (const entry of this.cache.values()) {
      if (now <= entry.expiresAt) {
        activeEntries++;
      } else {
        expiredEntries++;
      }
    }

    return {
      totalEntries: this.cache.size,
      activeEntries,
      expiredEntries,
      pendingRequests: this.requestQueue.size,
    };
  }
}

// Export singleton instance
export const requestCache = new RequestCacheManager();

// Auto-cleanup expired entries every 5 minutes
if (typeof window !== 'undefined') {
  setInterval(() => {
    const stats = requestCache.getStats();
    requestCache.clearExpired();
    
    if (stats.expiredEntries > 0) {
      console.log(`🧹 Cleaned ${stats.expiredEntries} expired cache entries`);
    }
  }, 5 * 60 * 1000);
}
